import { useCallback, useEffect, useState } from "react";
import { ConfirmModal } from "../lib/ConfirmModal.tsx";
import { AGENT_STAGES, type AgentStage } from "../../../src/sandbox/agent-stages.ts";
import { SANDBOX_BACKENDS } from "../../../src/sandbox/sandbox-backend.ts";
import { SANDBOX_SIZES, SIZE_MENU, type SandboxSize } from "../../../src/sandbox/sandbox-size.ts";
import { fetchJson } from "../lib/fetchJson.ts";
import { DISPATCH_PAUSED_MESSAGE } from "../lib/MaintenanceBanner.tsx";
import { Refusal, when } from "./bits.tsx";

// The Platform screen: the platform's own values in six panels (Agent,
// Models per stage, Sandbox, Try it, GitHub App, Dispatch). A save reaches every customer's next Run;
// no running Run moves. See ADR-0039 and ADR-0043.

/** Whether dispatch is paused platform-wide, and who last changed it when. */
export type DispatchState = {
  paused: boolean;
  changedBy: string | null;
  changedAt: string | null;
};

/** One platform value with who last set it and when; both null while it is still the default. */
export type PlatformValueState = {
  value: string;
  changedBy: string | null;
  changedAt: string | null;
};

/** One agent runtime the executor can drive, with the model names known to work on it. */
export type RuntimeEntry = {
  id: string;
  label: string;
  provider: string | null;
  models: string[];
};

/** The GitHub App as the platform sees it: configured when all three secrets are set, with the App ID and slug from configuration. */
export type GitHubAppStatus = {
  status: "configured" | "missing";
  appId: string | null;
  slug: string | null;
  secrets: { privateKey: boolean; clientSecret: boolean; webhookSecret: boolean };
};

/** The platform sandbox backend and the snapshot name per size; an empty name means that size is refused on Daytona. */
export type PlatformSandboxState = {
  backend: PlatformValueState;
  snapshots: Record<SandboxSize, PlatformValueState>;
};

/** What `GET /api/platform` returns: dispatch state, the agent pair, the runtimes on offer, the sandbox values and the GitHub App status. */
export type PlatformView = {
  dispatch: DispatchState;
  runtime: PlatformValueState;
  model: PlatformValueState & { unverified: boolean };
  runtimes: RuntimeEntry[];
  // Each stage's own model; an empty value follows `model`. `warning` is the
  // server's refusal sentence for a stage with its own model, else null.
  stageModels: Record<AgentStage, PlatformValueState & { runtime: string; unverified: boolean; warning: string | null }>;
  sandbox: PlatformSandboxState;
  // How many Try-it sandboxes may run at once per Organisation. Optional
  // so an older server's answer still renders.
  tryIt?: { maxRunning: PlatformValueState };
  // The server's words when the platform holds no credential for the pair's
  // provider, or could not tell which provider it needs; null when fine.
  credential: { warning: string | null };
  githubApp: GitHubAppStatus;
};

/**
 * The Platform screen: the Agent form, the per-stage models form, the Sandbox form, the read-only GitHub
 * App tile and the Pause/Resume dispatch controls. Reads `/api/platform` on
 * mount and takes the fresh view each save returns. Rendered by the admin
 * shell at `/platform`.
 */
export function PlatformPage(): JSX.Element {
  const [view, setView] = useState<PlatformView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const body = await fetchJson<{ platform: PlatformView }>("/api/platform");
      setView(body.platform);
      setLoadError(null);
    } catch (e) {
      setLoadError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <p className="lede">
        What is the platform&apos;s rather than any Organisation&apos;s. The runtime and model
        are one pair for every tenant: change them here and every customer&apos;s next Run
        uses them. A stage can run on a model of its own; one left empty runs on the
        platform model. The sandbox backend and its snapshots are where every Run is created,
        unless an Organisation is overridden. Pausing dispatch stops every
        Organisation&apos;s Runs from starting until you resume; Runs already running
        finish, and every customer sees a maintenance banner meanwhile.
      </p>

      <Refusal message={loadError} />

      <section className="panel" aria-label="agent">
        <div className="panel-head">
          <h2>Agent</h2>
          {view ? (
            <span className="meta" data-testid="agent-current">
              {view.runtime.value} · {view.model.value}
            </span>
          ) : null}
        </div>
        <div className="panel-body">
          {view === null ? (
            <p aria-live="polite">Loading…</p>
          ) : (
            <>
              {view.credential.warning ? (
                <div className="notice bad" role="alert" data-testid="credential-warning">
                  <div>
                    {view.credential.warning}. Add one on the <a href="/credentials">Credentials screen</a>; until
                    then every Run is refused with these words.
                  </div>
                </div>
              ) : null}
              <AgentForm view={view} onSaved={setView} />
            </>
          )}
        </div>
      </section>

      <section className="panel" aria-label="models per stage">
        <div className="panel-head">
          <h2>Models per stage</h2>
        </div>
        <div className="panel-body">
          {view === null ? <p aria-live="polite">Loading…</p> : <StageModelsForm view={view} onSaved={setView} />}
        </div>
      </section>

      <section className="panel" aria-label="sandbox">
        <div className="panel-head">
          <h2>Sandbox</h2>
          {view ? (
            <span className="meta" data-testid="sandbox-current">
              {view.sandbox.backend.value}
            </span>
          ) : null}
        </div>
        <div className="panel-body">
          {view === null ? <p aria-live="polite">Loading…</p> : <SandboxForm view={view} onSaved={setView} />}
        </div>
      </section>

      <section className="panel" aria-label="try it">
        <div className="panel-head">
          <h2>Try it</h2>
        </div>
        <div className="panel-body">
          {view === null ? <p aria-live="polite">Loading…</p> : <TryItForm view={view} onSaved={setView} />}
        </div>
      </section>

      <section className="panel" aria-label="GitHub App">
        <div className="panel-head">
          <h2>GitHub App</h2>
          {view ? (
            <span className={view.githubApp.status === "configured" ? "pill ok" : "pill warn"} data-testid="github-app-status">
              {view.githubApp.status}
            </span>
          ) : null}
        </div>
        <div className="panel-body">
          {view === null ? <p aria-live="polite">Loading…</p> : <GitHubAppTile app={view.githubApp} />}
        </div>
      </section>

      <section className="panel" aria-label="dispatch">
        <div className="panel-head">
          <h2>Dispatch</h2>
          {view ? (
            <span className={view.dispatch.paused ? "pill warn" : "pill ok"}>
              {view.dispatch.paused ? "paused" : "running"}
            </span>
          ) : null}
        </div>
        <div className="panel-body">
          {view === null ? <p aria-live="polite">Loading…</p> : <DispatchControls state={view.dispatch} onChanged={load} />}
        </div>
      </section>
    </>
  );
}

// The read-only tile: configured when all three secrets exist, otherwise
// which are missing, with the App ID and slug from configuration and never
// a secret.
function GitHubAppTile({ app }: { app: GitHubAppStatus }): JSX.Element {
  const missing = [
    !app.secrets.privateKey ? "private key" : null,
    !app.secrets.clientSecret ? "client secret" : null,
    !app.secrets.webhookSecret ? "webhook secret" : null,
  ].filter((s): s is string => s !== null);
  return (
    <>
      <dl className="kv" data-testid="github-app">
        <dt>App ID</dt>
        <dd className="mono">{app.appId ?? "not configured"}</dd>
        <dt>Slug</dt>
        <dd className="mono">{app.slug ?? "not configured"}</dd>
      </dl>
      <p className="tiny">
        {app.status === "configured"
          ? "The private key, client secret and webhook secret are all set."
          : `Missing: ${missing.join(", ")}. Put them on the Credentials screen.`}
        {app.appId === null ? " The App ID, client ID and slug are the admin app's configuration (githubApp.*)." : ""}
      </p>
    </>
  );
}

function changedLine(s: PlatformValueState): string {
  return s.changedAt ? `set ${when(s.changedAt)} by ${s.changedBy}` : "the platform default; never changed";
}

function AgentForm({ view, onSaved }: { view: PlatformView; onSaved: (v: PlatformView) => void }): JSX.Element {
  const [runtime, setRuntime] = useState(view.runtime.value);
  const [model, setModel] = useState(view.model.value);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setRuntime(view.runtime.value);
    setModel(view.model.value);
  }, [view.runtime.value, view.model.value]);

  const dirty = runtime !== view.runtime.value || model.trim() !== view.model.value;
  const listId = "platform-known-models";
  const knownModels = view.runtimes.find((r) => r.id === runtime)?.models ?? [];

  async function save(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const body = await fetchJson<{ platform: PlatformView }>("/api/platform/agent", {
        method: "PUT",
        body: JSON.stringify({ runtime, model }),
      });
      onSaved(body.platform);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <div className="field">
        <label htmlFor="platform-runtime">Runtime</label>
        <select id="platform-runtime" value={runtime} disabled={busy} onChange={(e) => setRuntime(e.target.value)}>
          {view.runtimes.map((r) => (
            <option key={r.id} value={r.id}>
              {/* A multi-provider runtime prints as such rather than borrowing
                  whichever provider its default model happens to use. */}
              {r.label} ({r.provider ?? "multi-provider"})
            </option>
          ))}
        </select>
        <p className="tiny">{changedLine(view.runtime)}</p>
      </div>
      <div className="field">
        <label htmlFor="platform-model">Model</label>
        <input
          id="platform-model"
          type="text"
          value={model}
          list={listId}
          disabled={busy}
          onChange={(e) => setModel(e.target.value)}
        />
        <datalist id={listId}>
          {knownModels.map((m) => (
            <option key={m} value={m} />
          ))}
        </datalist>
        <p className="tiny">
          {changedLine(view.model)}
          {view.model.unverified ? (
            <>
              {" · "}
              <span className="src" data-testid="model-unverified">unverified</span>
            </>
          ) : null}
        </p>
      </div>
      <button type="submit" className="btn primary sm" disabled={busy || !dirty || model.trim() === ""}>
        {busy ? "Saving…" : "Save"}
      </button>
      <Refusal message={error} />
    </form>
  );
}

const AGENT_STAGE_LABELS: Record<AgentStage, string> = {
  refinement: "Refinement",
  implementation: "Implementation",
  review: "Review",
  verification: "Verification",
  documentation: "Documentation",
  deployment: "Deployment",
  pull_request: "Pull request description",
};

// One box per stage. An empty box follows the platform model, and its
// placeholder names that model; saving sends every stage at once.
function StageModelsForm({ view, onSaved }: { view: PlatformView; onSaved: (v: PlatformView) => void }): JSX.Element {
  const current = view.stageModels;
  const currentModels = (): Record<AgentStage, string> => {
    const out = {} as Record<AgentStage, string>;
    for (const stage of AGENT_STAGES) out[stage] = current[stage].value;
    return out;
  };
  const [models, setModels] = useState<Record<AgentStage, string>>(currentModels);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Follow the saved values only: a save on another panel answers a fresh
  // view with the same stage values and must not wipe edits typed here.
  const savedKey = AGENT_STAGES.map((stage) => current[stage].value).join("\n");
  useEffect(() => {
    setModels(currentModels());
  }, [savedKey]);

  const dirty = AGENT_STAGES.some((stage) => models[stage].trim() !== current[stage].value);
  // One suggestion list per runtime a stage runs on.
  const stageRuntimes = [...new Set(AGENT_STAGES.map((stage) => current[stage].runtime))];
  const listIdOf = (runtime: string) => `stage-known-models-${runtime}`;

  async function save(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const body = await fetchJson<{ platform: PlatformView }>("/api/platform/models", {
        method: "PUT",
        body: JSON.stringify({ models }),
      });
      onSaved(body.platform);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <p className="tiny">
        Leave a stage empty to run it on the platform model. Each stage names the runtime its
        agent runs on; write the model the way that runtime names it.
      </p>
      {stageRuntimes.map((runtime) => (
        <datalist id={listIdOf(runtime)} key={runtime}>
          {(view.runtimes.find((r) => r.id === runtime)?.models ?? []).map((m) => (
            <option key={m} value={m} />
          ))}
        </datalist>
      ))}
      {AGENT_STAGES.map((stage) => {
        const id = `stage-model-${stage}`;
        const state = current[stage];
        return (
          <div className="field" key={stage}>
            <label htmlFor={id}>{AGENT_STAGE_LABELS[stage]}</label>
            <p className="tiny" data-testid={`stage-runtime-${stage}`}>runs on {state.runtime}</p>
            <input
              id={id}
              type="text"
              value={models[stage]}
              list={listIdOf(state.runtime)}
              disabled={busy}
              placeholder={`platform model (${view.model.value})`}
              onChange={(e) => setModels({ ...models, [stage]: e.target.value })}
            />
            <p className="tiny">
              {state.changedAt ? `set ${when(state.changedAt)} by ${state.changedBy}` : "never changed"}
              {state.unverified ? (
                <>
                  {" · "}
                  <span className="src" data-testid={`stage-unverified-${stage}`}>unverified</span>
                </>
              ) : null}
            </p>
            {state.warning ? (
              <p className="tiny bad-note" data-testid={`stage-warning-${stage}`}>
                {state.warning}. Add one on the <a href="/credentials">Credentials screen</a>.
              </p>
            ) : null}
          </div>
        );
      })}
      <button type="submit" className="btn primary sm" disabled={busy || !dirty}>
        {busy ? "Saving…" : "Save"}
      </button>
      <Refusal message={error} />
    </form>
  );
}

/**
 * The words beside an empty snapshot box, judged on the box as typed, so
 * clearing one says before the save that the size will be refused on Daytona.
 */
export const NO_SNAPSHOT_HINT = "no snapshot: this size is refused on Daytona";

function SandboxForm({ view, onSaved }: { view: PlatformView; onSaved: (v: PlatformView) => void }): JSX.Element {
  const current = view.sandbox;
  const currentSnapshots = (): Record<SandboxSize, string> => {
    const out = {} as Record<SandboxSize, string>;
    for (const size of SANDBOX_SIZES) out[size] = current.snapshots[size].value;
    return out;
  };
  const [backend, setBackend] = useState(current.backend.value);
  const [snapshots, setSnapshots] = useState<Record<SandboxSize, string>>(currentSnapshots);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Follow each read: the form shows what the server answered, and the
  // same values written back are not a change.
  useEffect(() => {
    setBackend(current.backend.value);
    setSnapshots(currentSnapshots());
  }, [current]);

  const dirty =
    backend !== current.backend.value ||
    SANDBOX_SIZES.some((size) => snapshots[size].trim() !== current.snapshots[size].value);

  async function save(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const body = await fetchJson<{ platform: PlatformView }>("/api/platform/sandbox", {
        method: "PUT",
        body: JSON.stringify({ backend, snapshots }),
      });
      onSaved(body.platform);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <div className="field">
        <label htmlFor="platform-sandbox-backend">Backend</label>
        <select
          id="platform-sandbox-backend"
          value={backend}
          disabled={busy}
          onChange={(e) => setBackend(e.target.value)}
        >
          {SANDBOX_BACKENDS.map((b) => (
            <option key={b} value={b}>
              {b}
            </option>
          ))}
        </select>
        <p className="tiny">{changedLine(current.backend)}</p>
      </div>
      {SANDBOX_SIZES.map((size) => {
        const id = `platform-snapshot-${size}`;
        return (
          <div className="field" key={size}>
            <label htmlFor={id}>{SIZE_MENU[size].label} snapshot</label>
            <input
              id={id}
              type="text"
              value={snapshots[size]}
              disabled={busy}
              placeholder={`tsv-sandbox-${size}-…`}
              onChange={(e) => setSnapshots({ ...snapshots, [size]: e.target.value })}
            />
            <p className="tiny">
              {changedLine(current.snapshots[size])}
              {snapshots[size].trim() === "" ? (
                <>
                  {" · "}
                  <span className="src" data-testid={`no-snapshot-${size}`}>{NO_SNAPSHOT_HINT}</span>
                </>
              ) : null}
            </p>
          </div>
        );
      })}
      <button type="submit" className="btn primary sm" disabled={busy || !dirty}>
        {busy ? "Saving…" : "Save"}
      </button>
      <Refusal message={error} />
    </form>
  );
}

// The cap on running Try-it sandboxes per Organisation, on the Sandbox
// form's pattern: follow each read, save only a change.
function TryItForm({ view, onSaved }: { view: PlatformView; onSaved: (v: PlatformView) => void }): JSX.Element {
  const current = view.tryIt?.maxRunning ?? { value: "3", changedBy: null, changedAt: null };
  const [maxRunning, setMaxRunning] = useState(current.value);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setMaxRunning(current.value);
  }, [current.value]);

  async function save(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const body = await fetchJson<{ platform: PlatformView }>("/api/platform/try-it", {
        method: "PUT",
        body: JSON.stringify({ maxRunning: maxRunning.trim() }),
      });
      onSaved(body.platform);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <div className="field">
        <label htmlFor="platform-try-it-max-running">Running Try-it sandboxes per Organisation</label>
        <input
          id="platform-try-it-max-running"
          type="number"
          min={1}
          step={1}
          value={maxRunning}
          disabled={busy}
          onChange={(e) => setMaxRunning(e.target.value)}
        />
        <p className="tiny">{changedLine(current)} · Ready ones are not counted.</p>
      </div>
      <button type="submit" className="btn primary sm" disabled={busy || maxRunning.trim() === current.value}>
        {busy ? "Saving…" : "Save"}
      </button>
      <Refusal message={error} />
    </form>
  );
}

function DispatchControls({ state, onChanged }: { state: DispatchState; onChanged: () => Promise<void> }): JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function verb(which: "pause" | "resume"): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await fetchJson(`/api/platform/${which}`, { method: "POST" });
      await onChanged();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  }

  let action: JSX.Element;
  if (state.paused) {
    action = (
      <button type="button" className="btn primary sm" disabled={busy} onClick={() => void verb("resume")}>
        {busy ? "Resuming…" : "Resume dispatch"}
      </button>
    );
  } else {
    action = (
      <>
        <button type="button" className="btn ghost sm" onClick={() => setConfirming(true)}>
          Pause dispatch
        </button>
        <ConfirmModal
          open={confirming}
          title="Pause dispatch"
          heading="Pause dispatch for every Organisation?"
          confirmLabel="Confirm pause"
          busyLabel="Pausing…"
          busy={busy}
          error={error}
          onConfirm={() => void verb("pause")}
          onCancel={() => setConfirming(false)}
        >
          <p>
            Nothing starts anywhere until you resume: queued Runs wait, and every verb that
            would start one is refused with the maintenance message. Running work finishes.
          </p>
        </ConfirmModal>
      </>
    );
  }

  return (
    <>
      <p>
        {state.paused
          ? `${DISPATCH_PAUSED_MESSAGE} — since ${when(state.changedAt!)} by ${state.changedBy}. Nothing starts in any Organisation until you resume.`
          : state.changedAt
            ? `Runs are being dispatched. Last resumed ${when(state.changedAt)} by ${state.changedBy}.`
            : "Runs are being dispatched. Dispatch has never been paused."}
      </p>
      {action}
      <Refusal message={error} />
    </>
  );
}
